/**
 * Card #536: say WHICH rows of an item CSV stop the import, not only that one did.
 *
 * The dialog used to stop at the first required field with a problem in any row
 * and show one generic sentence ("Price A is a required field."), so the user
 * had to search the file by hand. These helpers find every problem, then group
 * them so a file with the same gap in 40 rows reads as one line, not 40.
 *
 * Pure functions: no Angular, no translation. The dialog turns the groups into
 * translated text.
 */

/** Why a row was refused. Same two checks the dialog already made, unchanged. */
export type CsvRowProblemReason = 'missing' | 'invalid_price';

/** A required field and the file column it is mapped to. */
export interface CsvRequiredColumn {
  /** field key, e.g. "name" | "category" | "priceA" */
  fieldId: string;
  /** what the user sees for the field, e.g. "Price A" (already translated) */
  label: string;
  /** the file column chosen for it */
  column: string;
  /** price fields also have to look like a number */
  isPrice?: boolean;
}

/** One file row as the dialog holds it, with its row number in the spreadsheet. */
export interface CsvNumberedRow {
  /** spreadsheet row number: the header is row 1, so the first item is row 2 */
  row: number | null;
  data: Record<string, any>;
}

export interface CsvRowProblem {
  row: number | null;
  /** how the user recognises the item: its name, its code, or both */
  item: string;
  fieldId: string;
  label: string;
  column: string;
  reason: CsvRowProblemReason;
  /** the value that was refused (invalid_price only) */
  value?: string;
}

/** All the rows with the same problem on the same field. */
export interface CsvRowProblemGroup {
  fieldId: string;
  label: string;
  column: string;
  reason: CsvRowProblemReason;
  problems: CsvRowProblem[];
}

/**
 * The price check the dialog already applied, kept identical: digits, optional
 * thousands separators, any number of decimals ("112.211" is valid).
 */
export const CSV_PRICE_PATTERN = /^[0-9]+(?:,[0-9]{3})*(?:\.[0-9]+)?$/;

/**
 * The dialog's "missing" rule, kept identical: the cell is empty or the column
 * is absent from the row. A cell holding only spaces is NOT missing (it never
 * was), so no file that imported before is refused now.
 */
export const isMissingCsvValue = (raw: unknown): boolean =>
  raw === '' || raw === undefined || raw === null;

/**
 * Every problem in the file, in row order.
 *
 * "Missing" keeps the dialog's existing rule exactly (the cell is empty or the
 * column is absent from that row), so no file that imported before is refused
 * now; only the reporting changes.
 *
 * @param rows      the file's rows, each with its spreadsheet row number
 * @param required  the required fields that ARE mapped to a column (an unmapped
 *                  field is a column problem, reported by the dialog's own warning)
 * @param identify  how to name the item in a row, e.g. its name and code
 */
export function findCsvRowProblems(
  rows: CsvNumberedRow[],
  required: CsvRequiredColumn[],
  identify: (data: Record<string, any>) => string,
): CsvRowProblem[] {
  const problems: CsvRowProblem[] = [];
  (rows || []).forEach(({ row, data }) => {
    (required || []).forEach((field) => {
      const raw = data?.[field.column];
      const base = { row, fieldId: field.fieldId, label: field.label, column: field.column };
      if (isMissingCsvValue(raw)) {
        problems.push({ ...base, item: identify(data), reason: 'missing' });
        return;
      }
      if (field.isPrice && typeof raw === 'string' && !CSV_PRICE_PATTERN.test(raw.trim())) {
        problems.push({ ...base, item: identify(data), reason: 'invalid_price', value: raw });
      }
    });
  });
  return problems;
}

/**
 * Group problems by (field, reason), keeping the order in which each group was
 * first met, and rows in file order inside each group.
 */
export function groupCsvRowProblems(problems: CsvRowProblem[]): CsvRowProblemGroup[] {
  const groups = new Map<string, CsvRowProblemGroup>();
  (problems || []).forEach((p) => {
    const key = `${p.fieldId}\u0000${p.reason}`;
    let group = groups.get(key);
    if (!group) {
      group = { fieldId: p.fieldId, label: p.label, column: p.column, reason: p.reason, problems: [] };
      groups.set(key, group);
    }
    group.problems.push(p);
  });
  return [...groups.values()];
}

/**
 * "4, 9, 14" -- or, past `max` rows, "4, 9, 14, …" so a 2,000-row file does not
 * turn the dialog into a wall of numbers. The group's count says how many there are.
 */
export function formatCsvRowList(rows: Array<number | null> | null | undefined, max = 10): string {
  const safe = rows ?? [];
  const cap = Math.max(1, max);
  const list = safe.slice(0, cap).map((r) => (r === null ? '?' : r)).join(', ');
  return safe.length > cap ? `${list}, …` : list;
}

/** A refused cell value, cut to `max` characters so one long cell cannot flood the list. */
export function truncateCsvValue(value: unknown, max = 40): string {
  const s = String(value ?? '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * Name an item the way the card asks: "Blue Mug" (SKU-0231). Falls back to
 * whichever of the two the row has, or '' when it has neither.
 */
export function describeCsvItem(name: unknown, code: unknown): string {
  const n = typeof name === 'string' ? truncateCsvValue(name.trim(), 60) : '';
  const c = typeof code === 'string' ? truncateCsvValue(code.trim(), 60) : '';
  if (n && c) return `"${n}" (${c})`;
  if (n) return `"${n}"`;
  return c ? `(${c})` : '';
}
